/**
 * useDiagnosticsSnapshot — fetches diagnostics.snapshot.get from the plugin.
 *
 * @param {{ enabled: boolean }} options
 *   enabled — set to pluginSnapshot?.capabilities?.diagnosticSnapshotV1 === true
 *
 * @returns {{
 *   snapshot: NormalizedSnapshot|null,
 *   capabilities: { indexSizeBytes: boolean, latencySummary: boolean },
 *   warnings: Array<{ metric: string, code: string }>,
 *   isLoading: boolean,
 *   error: string|null,
 * }}
 *
 * NormalizedSnapshot (camelCase):
 * {
 *   pluginVersion, coreVersion, adminAppVersion, schemaVersion,
 *   capabilities: string[],
 *   activeIndexPresent: boolean,
 *   activeIndexRecords: number|null,
 *   passiveIndexRecords: number|null,
 *   activeIndexSizeBytes: null,
 *   passiveIndexSizeBytes: null,
 *   lastIndexingStartedAt: string|null,
 *   lastIndexingFinishedAt: string|null,
 *   lastIndexingDurationMs: number|null,
 *   lastIndexingProductsPerSecond: number|null,
 *   lastIndexingStatus: string|null,
 *   lastIndexingErrorCode: string|null,
 *   phpVersion: string|null,
 *   phpMemoryLimit: string|null,
 *   databaseServerVersion: string|null,
 *   databaseNameHash: string|null,
 *   serverSoftware: string|null,
 *   moduleRuntimeUserKnown: boolean,
 *   recentQueryCount: null,
 *   recentZeroResultCount: null,
 *   recentErrorCount: null,
 *   recentP95LatencyMs: null,
 * }
 */

import { useState, useEffect, useRef } from 'react';
import { BridgeClient } from '../../../app/service/bridge/BridgeClient';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';

function normalizeSnapshot(raw) {
  const s = raw || {};
  return {
    pluginVersion: s.plugin_version ?? null,
    coreVersion: s.core_version ?? null,
    adminAppVersion: s.admin_app_version ?? null,
    schemaVersion: s.schema_version ?? null,
    capabilities: Array.isArray(s.capabilities) ? s.capabilities : [],
    activeIndexPresent: s.active_index_present ?? false,
    activeIndexRecords: s.active_index_records ?? null,
    passiveIndexRecords: s.passive_index_records ?? null,
    activeIndexSizeBytes: s.active_index_size_bytes ?? null,
    passiveIndexSizeBytes: s.passive_index_size_bytes ?? null,
    lastIndexingStartedAt: s.last_indexing_started_at ?? null,
    lastIndexingFinishedAt: s.last_indexing_finished_at ?? null,
    lastIndexingDurationMs: s.last_indexing_duration_ms ?? null,
    lastIndexingProductsPerSecond: s.last_indexing_products_per_second ?? null,
    lastIndexingStatus: s.last_indexing_status ?? null,
    lastIndexingErrorCode: s.last_indexing_error_code ?? null,
    phpVersion: s.php_version ?? null,
    phpMemoryLimit: s.php_memory_limit ?? null,
    databaseServerVersion: s.database_server_version ?? null,
    databaseNameHash: s.database_name_hash ?? null,
    serverSoftware: s.server_software ?? null,
    moduleRuntimeUserKnown: s.module_runtime_user_known ?? false,
    recentQueryCount: s.recent_query_count ?? null,
    recentZeroResultCount: s.recent_zero_result_count ?? null,
    recentErrorCount: s.recent_error_count ?? null,
    recentP95LatencyMs: s.recent_p95_latency_ms ?? null,
  };
}

function normalizeCapabilities(raw) {
  return {
    indexSizeBytes: !!(raw?.index_size_bytes),
    latencySummary: !!(raw?.latency_summary),
  };
}

export function useDiagnosticsSnapshot({ enabled }) {
  const t = useLocalizedMessage();
  const [snapshot, setSnapshot] = useState(null);
  const [capabilities, setCapabilities] = useState({ indexSizeBytes: false, latencySummary: false });
  const [warnings, setWarnings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const bridgeRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setSnapshot(null);
      setCapabilities({ indexSizeBytes: false, latencySummary: false });
      setWarnings([]);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;

    if (!bridgeRef.current) {
      bridgeRef.current = new BridgeClient();
    }

    setIsLoading(true);
    setError(null);

    bridgeRef.current
      .getDiagnosticsSnapshot()
      .then((response) => {
        if (cancelled) return;

        if (response.result === 'error') {
          setError(response.message || response.error || t('operator.diag.snapshot.loadFailed'));
          setIsLoading(false);
          return;
        }

        setSnapshot(normalizeSnapshot(response.snapshot));
        setCapabilities(normalizeCapabilities(response.capabilities));
        setWarnings(Array.isArray(response.warnings) ? response.warnings : []);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || err?.error || t('operator.diag.snapshot.loadFailed'));
        setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [enabled, t]);

  return {
    snapshot,
    capabilities,
    warnings,
    isLoading,
    error,
  };
}
